export function machineQrToken(raw: string) {
  let token = raw.trim();
  try { token = new URL(token).searchParams.get('t') ?? token; } catch { /* raw token */ }
  if (!/^[a-f0-9]{64}$/i.test(token)) throw new Error('Đây không phải mã QR máy. Hãy quét mã được dán trên máy.');
  return token;
}

// Camera teardown can be slow on phones. It must not delay machine lookup or
// the confirmation screen. The caller keeps the scanner DOM mounted for cleanup.
export async function identifyMachine<T>(raw: string, lookup: (token: string) => Promise<T>, stopCamera: () => Promise<void>) {
  const token = machineQrToken(raw);
  void stopCamera().catch(() => {});
  return { ...await lookup(token), qrToken: token };
}

export const machineCameraConstraints: MediaTrackConstraints = {
  facingMode: 'environment',
};
export const machineCameraConfig = {
  fps: 15,
  disableFlip: true,
  // html5-qrcode's first argument only accepts one selector key. Full camera
  // constraints belong here; otherwise it rejects BEFORE requesting permission.
  videoConstraints: {
    facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 },
  } satisfies MediaTrackConstraints,
  qrbox: (width: number, height: number) => {
    const side = Math.max(50, Math.min(320, Math.floor(Math.min(width, height) * 0.85)));
    return { width: side, height: side };
  },
};

export type MachineCameraIssue = {
  kind: 'permission' | 'busy' | 'missing' | 'constraints' | 'unsupported' | 'technical';
  message: string;
};

// html5-qrcode wraps DOMExceptions in strings, so preserve classification for
// both native exceptions and its "Error getting userMedia, error = ..." form.
export function machineCameraIssue(error: unknown): MachineCameraIssue {
  const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  if (/NotAllowedError|PermissionDeniedError|SecurityError/i.test(detail)) {
    return { kind: 'permission', message: 'Trình duyệt chưa cho app mở camera. Bấm “Cho phép camera” để thử lại; nếu có cửa sổ hỏi quyền, chọn “Cho phép”.' };
  }
  if (/NotReadableError|TrackStartError|AbortError|InvalidStateError/i.test(detail)) {
    return { kind: 'busy', message: 'Camera đang bận hoặc tạm thời chưa mở được. Đóng ứng dụng đang dùng camera, quay lại app và bấm thử lại.' };
  }
  if (/NotFoundError|DevicesNotFoundError/i.test(detail)) {
    return { kind: 'missing', message: 'Không tìm thấy camera trên thiết bị này. Bạn có thể chụp mã bằng điện thoại rồi chọn ảnh để quét.' };
  }
  if (/OverconstrainedError|ConstraintNotSatisfiedError/i.test(detail)) {
    return { kind: 'constraints', message: 'Camera chưa đáp ứng cấu hình quét. Thử mở lại hoặc chọn ảnh mã QR để quét.' };
  }
  if (/secure context|camera streaming not supported|navigator\.mediaDevices not supported/i.test(detail)) {
    return { kind: 'unsupported', message: 'Camera không khả dụng trong cửa sổ này. Mở app bằng trình duyệt qua HTTPS hoặc chọn ảnh mã QR để quét.' };
  }
  return { kind: 'technical', message: 'Chưa khởi động được bộ quét. Đây chưa chắc là lỗi quyền camera. Thử lại; nếu vẫn lỗi, tải lại app hoặc chọn ảnh mã QR.' };
}
